// #region imports

// --- Angular
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';

// --- RxJs
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  combineLatest,
  filter,
  fromEvent,
} from 'rxjs';

// --- 3rd Party
import { NgxSpinnerService } from 'ngx-spinner';
import { User as OidcUser } from 'oidc-client';
import { TranslateService } from '@ngx-translate/core';
import { JL } from 'jsnlog';

// --- HS Code
import { environment } from '../environments/environment';
import {
  ApplicationError,
  Constants,
  LanguageModel,
  ToastEventModel,
} from './models';
import { fadeAnimation } from './app.animation';
import { ConsentService } from './core';
import {
  AppService,
  AppToastService,
  CommunicationsBaseService,
  EnrichedUserDetailsBaseService,
  EnvService,
  IdleTimeoutService,
  LanguageDomBaseService,
  LanguagesBaseService,
  OidcService,
  ProfileBaseService,
  SurveyBaseService,
  TermsAndConditionsBaseService,
  UserDetailsBaseService,
} from './core/services';

declare let gtag: Function;

// #endregion

@Component({
  selector: 'hs-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [fadeAnimation],
})
export class AppComponent implements OnInit, OnDestroy {
  JL: JL.JSNLog;

  title = 'Heartstyles Indicator - Enterprise Edition';

  error$: Observable<ApplicationError | null> =
    new Observable<ApplicationError | null>();
  isLoading$: Observable<boolean> = new Observable<boolean>();
  identity$: Observable<OidcUser | null> = new Observable<OidcUser | null>();
  languages$: Observable<LanguageModel[]> = new Observable<LanguageModel[]>();
  selectedLanguage$: Observable<LanguageModel> =
    new Observable<LanguageModel>();

  isLoading: boolean = false;
  isLoadingSubscription: Subscription = Subscription.EMPTY;

  identity: OidcUser | null = null;
  identitySubscription: Subscription = Subscription.EMPTY;

  isRouting = false;

  idleState$: Observable<string | null>;

  imageRootUrl$: Subject<string> = new BehaviorSubject<string>('');
  selectedLanguageSubscription: Subscription = Subscription.EMPTY;

  private _hasAnalyticsConsent = false;
  analyticsConsentSubscription: Subscription = Subscription.EMPTY;

  connectionStatusMessage: string = '';
  connectionStatus: string = '';
  onlineSubscription: Subscription = Subscription.EMPTY;
  offlineSubscription: Subscription = Subscription.EMPTY;
  onlineEvent$ = fromEvent(window, 'online');
  offlineEvent$ = fromEvent(window, 'offline');

  private _onPageShow = (event: PageTransitionEvent): void => {
    // When leaving to login, we set the loading overlay on, but when returning with 
    // back navigationwe need to clear it
    if (event.persisted) {
      this.appService.resetLoading();
    }
  };

  constructor(
    @Inject('JSNLOG') JL: JL.JSNLog,
    private appService: AppService,
    private communicationsService: CommunicationsBaseService,
    private consentService: ConsentService,
    private enrichedUserDetailsService: EnrichedUserDetailsBaseService,
    private envService: EnvService,
    private idleTimeoutService: IdleTimeoutService,
    private languageDomService: LanguageDomBaseService,
    private languageService: LanguagesBaseService,
    private oidcService: OidcService,
    private profileService: ProfileBaseService,
    private router: Router,
    private spinner: NgxSpinnerService,
    private surveyService: SurveyBaseService,
    private termsAndConditionsService: TermsAndConditionsBaseService,
    private toastService: AppToastService,
    private translate: TranslateService,
    private userDetailsService: UserDetailsBaseService
  ) {
    this.JL = JL;
    this.idleState$ = new Observable<string | null>();

    this.translate.setDefaultLang(Constants.DEFAULT_LOCALE);
    this.translate.use(Constants.DEFAULT_LOCALE);
    this.isLoading = false;
    this.imageRootUrl$ = new Subject<string>();

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this._reportAnalyticsPageView(event.urlAfterRedirects);
      }
    });
  }

  ngOnInit(): void {
    this.JL().debug('EE - Heartstyles Survey application initialised');

    this.idleState$ = this.idleTimeoutService.idleState$;

    this.isLoadingSubscription = this.appService.isLoading$.subscribe(
      (isLoading) => {
        this.isLoading = isLoading;
        if (isLoading) {
          this.spinner.show();
          return;
        }
        this.spinner.hide();
      }
    );

    this.identitySubscription = this.oidcService.identity$.subscribe(
      (oidcUser: OidcUser | null) => {
        this.identity = oidcUser;

        if (oidcUser) {
          this.profileService.loadProfile();
          this.communicationsService.loadUserCommunicationPreferences();
          this.userDetailsService.loadSurveyUserDetails();
          this.termsAndConditionsService.loadUserAgreementPreferences();
          this.languageService.initialiseLanguage(oidcUser.profile.locale);

          if (oidcUser.profile[Constants.CLAIMS_SURVEY_PARTICIPANT] === 'True') {
            this.surveyService.getMostRecentParticipantSurvey();
            this.enrichedUserDetailsService.getUserDetails(oidcUser.profile[Constants.CLAIMS_COMPANY_ID]);
          }
        } else {
          this.enrichedUserDetailsService.getUserDetails(null);
        }
      }
    );

    this.error$ = this.appService.lastError$;
    this.isLoading$ = this.appService.isLoading$;
    this.identity$ = this.oidcService.identity$;
    this.languages$ = this.languageService.languages$;
    this.selectedLanguage$ = this.languageService.selectedLanguage$;

    this.languageService.loadLanguages();
    this.oidcService.getOidcUser();
    this.consentService.init(Constants.DEFAULT_LOCALE);

    // Fire an explicit GA event on the transition to granted consent
    this.analyticsConsentSubscription =
      this.consentService.analyticsConsent$.subscribe((granted) => {
        if (granted && !this._hasAnalyticsConsent) {
          this._reportAnalyticsConsentAccepted();
        }
        this._hasAnalyticsConsent = granted;
      });

    this.selectedLanguageSubscription = combineLatest([
      this.languageService.selectedLanguage$.pipe(
        filter((language) => language !== null)
      ),
      this.oidcService.isMobileUser$,
    ]).subscribe(([language, isMobileUser]) => {
      const translateLanguageAndCulture = language.cultureCode
        ? `${language.isoTwoLetterName}-${language.cultureCode}`
        : `${language.isoTwoLetterName}`;
      // Mobile (webview) sessions are pinned to English throughout the app,
      // including the survey flow and the cookie consent banner (#2909, #3398).
      const activeLocale = isMobileUser ? Constants.DEFAULT_LOCALE : translateLanguageAndCulture;
      this.consentService.setLanguage(activeLocale);
      this.translate.use(activeLocale);
      this.imageRootUrl$.next(
        `/assets/images/i18n/${translateLanguageAndCulture}/`
      );
    });

    this.router.events.subscribe((routerEvent: Event) => {
      this.checkRouterEvent(routerEvent);
    });

    this.onlineSubscription = this.onlineEvent$.subscribe((e) => {
      this.connectionStatusMessage = this.translate.instant(
        'connectionStatusMessageOnline'
      );
      this.connectionStatus = 'online';
    });

    this.offlineSubscription = this.offlineEvent$.subscribe((e) => {
      this.connectionStatusMessage = this.translate.instant(
        'connectionStatusMessageOffline'
      );
      this.connectionStatus = 'offline';
    });

    this.languageDomService.init();

    window.addEventListener('pageshow', this._onPageShow);
  }

  ngOnDestroy(): void {
    this.isLoadingSubscription.unsubscribe();
    this.isLoadingSubscription = Subscription.EMPTY;
    this.identitySubscription.unsubscribe();
    this.identitySubscription = Subscription.EMPTY;

    if (this.onlineSubscription) {
      this.onlineSubscription.unsubscribe();
      this.onlineSubscription = Subscription.EMPTY;
    }

    if (this.offlineSubscription) {
      this.offlineSubscription.unsubscribe();
      this.offlineSubscription = Subscription.EMPTY;
    }

    if (this.selectedLanguageSubscription) {
      this.selectedLanguageSubscription.unsubscribe();
      this.selectedLanguageSubscription = Subscription.EMPTY;
    }

    if (this.analyticsConsentSubscription) {
      this.analyticsConsentSubscription.unsubscribe();
      this.analyticsConsentSubscription = Subscription.EMPTY;
    }

    this.languageDomService.destroy();

    window.removeEventListener('pageshow', this._onPageShow);
  }

  private _reportAnalyticsPageView(pagePath: string): void {
    const gtagId =
      this.envService.gtagPropertyId ||
      environment.gtagPropertyId ||
      'DEVELOPMENT';
    gtag('config', gtagId, {
      page_path: pagePath,
    });
  }

  private _reportAnalyticsConsentAccepted(): void {
    gtag('event', 'analytics_consent_accepted');
  }

  checkRouterEvent(routerEvent: Event): void {
    if (routerEvent instanceof NavigationStart) {
      this.isRouting = true;
    }

    if (
      routerEvent instanceof NavigationEnd ||
      routerEvent instanceof NavigationCancel ||
      routerEvent instanceof NavigationError
    ) {
      this.isRouting = false;
      // scroll to the top: on route change
      window.scrollTo(0, 0);
    }
  }

  clearAlert(): void {
    this.appService.setLastError(null);
  }

  onNetworkCallToAction(): void {
    window.location.reload();
  }

  onNetworkAlertClose(): void {}

  onToastLinkClicked(toastEvent: ToastEventModel): void {
    this.toastService.clickNotify(toastEvent);
  }
}
